import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { creditos, hora } from "../lib/formato";
import {
  Aviso,
  Cabecera,
  Campo,
  Cargando,
  Etiqueta,
  Modal,
  Panel,
  TarjetaDato,
  Vacio,
} from "../componentes/piezas";
import BuscadorSocios, { type SocioResumen } from "../componentes/BuscadorSocios";
import { useSesion } from "../lib/sesion";
import { useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

interface ProductoBarra {
  id: string;
  nombre: string;
  grupo: string;
  precioCentimos: number;
  controlarStock: boolean;
  stockUnidades: number;
  activo: boolean;
  orden: number;
}

interface Venta {
  id: string;
  numero: number;
  totalCentimos: number;
  metodoPago: string;
  estado: string;
  fecha: string;
  socio: { numeroSocio: number; nombre: string; apellidos: string } | null;
  usuario: { nombre: string } | null;
  lineas: { id: string; nombre: string; cantidad: number; totalCentimos: number }[];
}

/**
 * Barra: cafés, refrescos y snacks.
 *
 * Pensada para cobrar rápido: se toca el producto, se elige como paga y ya.
 * El socio solo hace falta para cobrar de la cartera; pararse a buscarlo para
 * un café en efectivo sería hacer la barra más lenta que el bar de al lado.
 */
export default function Barra() {
  const { t } = useIdioma();
  const cliente = useQueryClient();
  const { puede } = useSesion();

  const [cesta, setCesta] = useState<Record<string, number>>({});
  const [metodoPago, setMetodoPago] = useState("EFECTIVO");
  const [socio, setSocio] = useState<SocioResumen | null>(null);
  const [error, setError] = useState("");
  const [mensaje, setMensaje] = useState("");
  const [cobrando, setCobrando] = useState(false);
  const [gestionando, setGestionando] = useState(false);

  const productos = useQuery({
    queryKey: ["barra-productos"],
    queryFn: () => api<ProductoBarra[]>("/barra/productos"),
  });
  const ventas = useQuery({ queryKey: ["barra-ventas"], queryFn: () => api<Venta[]>("/barra/ventas") });

  const porGrupo = useMemo(() => {
    const mapa = new Map<string, ProductoBarra[]>();
    for (const p of productos.data ?? []) {
      const lista = mapa.get(p.grupo) ?? [];
      lista.push(p);
      mapa.set(p.grupo, lista);
    }
    return [...mapa.entries()];
  }, [productos.data]);

  const totalCentimos = Object.entries(cesta).reduce((suma, [id, cantidad]) => {
    const p = (productos.data ?? []).find((x) => x.id === id);
    return suma + (p?.precioCentimos ?? 0) * cantidad;
  }, 0);

  const unidades = Object.values(cesta).reduce((s, n) => s + n, 0);
  const faltaSocio = metodoPago === "CARTERA" && !socio;

  const sumar = (id: string, cuantos: number) =>
    setCesta((c) => {
      const nuevo = (c[id] ?? 0) + cuantos;
      const copia = { ...c };
      if (nuevo <= 0) delete copia[id];
      else copia[id] = nuevo;
      return copia;
    });

  const vaciar = () => {
    setCesta({});
    setSocio(null);
    setMetodoPago("EFECTIVO");
  };

  async function cobrar() {
    setError("");
    setMensaje("");
    setCobrando(true);
    try {
      const venta = await api<{ numero: number }>("/barra/ventas", {
        metodo: "POST",
        cuerpo: {
          lineas: Object.entries(cesta).map(([productoBarraId, cantidad]) => ({ productoBarraId, cantidad })),
          metodoPago,
          socioId: socio?.id ?? null,
        },
      });
      setMensaje(t("ba.cobrado", { importe: creditos(totalCentimos), numero: venta.numero }));
      vaciar();
      void cliente.invalidateQueries({ queryKey: ["barra-ventas"] });
      void cliente.invalidateQueries({ queryKey: ["barra-productos"] });
      void cliente.invalidateQueries({ queryKey: ["caja-actual"] });
      void cliente.invalidateQueries({ queryKey: ["panel"] });
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : t("ba.falloCobrar"));
    } finally {
      setCobrando(false);
    }
  }

  const cobradas = (ventas.data ?? []).filter((v) => v.estado === "COMPLETADA");
  const totalDelDia = cobradas.reduce((s, v) => s + v.totalCentimos, 0);
  const cobrosDeHoy = cobradas.length;

  return (
    <div>
      <Cabecera titulo={t("ba.titulo")} descripcion={t("ba.descripcion")}>
        {puede("gestionarBarra") && (
          <button className="boton secundario" onClick={() => setGestionando(true)}>
            {t("ba.carta")}
          </button>
        )}
      </Cabecera>

      <Aviso tipo="error">{error}</Aviso>
      <Aviso tipo="ok">{mensaje}</Aviso>

      {productos.isLoading ? (
        <Cargando />
      ) : porGrupo.length === 0 ? (
        <Panel sinCuerpo>
          <Vacio>
            {t("ba.vacia")}
            {puede("gestionarBarra") ? t("ba.vaciaAdmin") : t("ba.vaciaSocio")}
          </Vacio>
        </Panel>
      ) : (
        <div className="rejilla dos" style={{ alignItems: "start" }}>
          <div className="columna" style={{ gap: 16 }}>
            {porGrupo.map(([grupo, lista]) => (
              <Panel key={grupo} titulo={grupo}>
                <div className="barra-productos">
                  {lista.map((p) => {
                    const sinStock = p.controlarStock && p.stockUnidades <= 0;
                    return (
                      <button
                        key={p.id}
                        className="barra-producto"
                        disabled={sinStock}
                        onClick={() => sumar(p.id, 1)}
                        title={sinStock ? t("ba.sinExistencias") : undefined}
                      >
                        <strong>{p.nombre}</strong>
                        <span className="tabular">{creditos(p.precioCentimos)}</span>
                        {p.controlarStock && (
                          <span className="suave pequeno">
                            {sinStock ? t("ba.agotado") : t("ba.quedan", { n: p.stockUnidades })}
                          </span>
                        )}
                        {cesta[p.id] ? <span className="contador">{cesta[p.id]}</span> : null}
                      </button>
                    );
                  })}
                </div>
              </Panel>
            ))}
          </div>

          <div className="columna" style={{ gap: 16 }}>
            <Panel titulo={t("ba.cobro")}>
              {unidades === 0 ? (
                <p className="suave" style={{ margin: 0 }}>
                  {t("ba.tocaProducto")}
                </p>
              ) : (
                <>
                  <table className="tabla">
                    <tbody>
                      {Object.entries(cesta).map(([id, cantidad]) => {
                        const p = (productos.data ?? []).find((x) => x.id === id);
                        if (!p) return null;
                        return (
                          <tr key={id}>
                            <td>
                              {p.nombre}
                              <div className="suave pequeno">
                                {t("ba.cadaUno", { importe: creditos(p.precioCentimos) })}
                              </div>
                            </td>
                            <td className="num">
                              <div className="fila" style={{ gap: 6, justifyContent: "flex-end" }}>
                                <button className="boton fantasma" onClick={() => sumar(id, -1)}>
                                  −
                                </button>
                                <span className="tabular" style={{ minWidth: 24, textAlign: "center" }}>
                                  {cantidad}
                                </span>
                                <button className="boton fantasma" onClick={() => sumar(id, 1)}>
                                  +
                                </button>
                              </div>
                            </td>
                            <td className="num tabular">{creditos(p.precioCentimos * cantidad)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  <div className="fila separada" style={{ margin: "14px 0" }}>
                    <span>{t("m.total")}</span>
                    <strong className="tabular" style={{ fontSize: "1.6rem" }}>
                      {creditos(totalCentimos)}
                    </strong>
                  </div>

                  <div className="fila" style={{ gap: 8, marginBottom: 12 }}>
                    {["EFECTIVO", "CARTERA", "TARJETA"].map((m) => (
                      <button
                        key={m}
                        className={`boton ${metodoPago === m ? "" : "secundario"}`}
                        onClick={() => setMetodoPago(m)}
                      >
                        {t(`pago.${m}` as Clave)}
                      </button>
                    ))}
                  </div>

                  {metodoPago === "CARTERA" && (
                    <div style={{ marginBottom: 12 }}>
                      {socio ? (
                        <div className="fila separada">
                          <span>
                            #{socio.numeroSocio} {socio.nombre} {socio.apellidos}
                          </span>
                          <button className="boton fantasma" onClick={() => setSocio(null)}>
                            {t("m.cambiar")}
                          </button>
                        </div>
                      ) : (
                        <BuscadorSocios alElegir={setSocio} />
                      )}
                    </div>
                  )}

                  <div className="fila" style={{ gap: 8 }}>
                    <button className="boton secundario" onClick={vaciar}>
                      {t("ba.vaciar")}
                    </button>
                    <button
                      className="boton grande"
                      style={{ flex: 1 }}
                      disabled={cobrando || faltaSocio}
                      onClick={() => void cobrar()}
                    >
                      {cobrando ? t("ba.cobrando") : t("ba.cobrar", { total: creditos(totalCentimos) })}
                    </button>
                  </div>

                  {faltaSocio && (
                    <p className="suave pequeno" style={{ marginBottom: 0 }}>
                      {t("ba.faltaSocio")}
                    </p>
                  )}
                </>
              )}
            </Panel>

            <TarjetaDato
              etiqueta={t("ba.hoy")}
              valor={creditos(totalDelDia)}
              pie={cobrosDeHoy === 1 ? t("ba.unCobro") : t("ba.cobros", { n: cobrosDeHoy })}
            />

            <Panel titulo={t("ba.ultimos")} sinCuerpo>
              {(ventas.data ?? []).length === 0 ? (
                <Vacio>{t("ba.sinCobros")}</Vacio>
              ) : (
                <div className="tabla-envoltorio">
                  <table className="tabla">
                    <tbody>
                      {(ventas.data ?? []).slice(0, 12).map((v) => (
                        <tr key={v.id} style={v.estado === "ANULADA" ? { opacity: 0.5 } : undefined}>
                          <td className="pequeno suave">{hora(v.fecha)}</td>
                          <td className="pequeno">
                            {v.lineas.map((l) => `${l.cantidad}× ${l.nombre}`).join(", ")}
                            {v.socio && (
                              <div className="suave">
                                #{v.socio.numeroSocio} {v.socio.nombre}
                              </div>
                            )}
                          </td>
                          <td className="num tabular">
                            {creditos(v.totalCentimos)}
                            {v.estado === "ANULADA" && <Etiqueta tipo="roja">{t("ba.anulada")}</Etiqueta>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          </div>
        </div>
      )}

      {gestionando && (
        <DialogoCarta
          productos={productos.data ?? []}
          alCerrar={() => setGestionando(false)}
          alCambiar={() => void cliente.invalidateQueries({ queryKey: ["barra-productos"] })}
        />
      )}
    </div>
  );
}

/** Alta y edición de lo que vende la barra. */
function DialogoCarta({
  productos,
  alCerrar,
  alCambiar,
}: {
  productos: ProductoBarra[];
  alCerrar: () => void;
  alCambiar: () => void;
}) {
  const { t } = useIdioma();
  const [datos, setDatos] = useState({
    nombre: "",
    grupo: t("ba.c.grupoPorDefecto"),
    precioEuros: "",
    controlarStock: false,
    stockUnidades: "",
  });
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  async function crear() {
    setError("");
    setEnviando(true);
    try {
      await api("/barra/productos", {
        metodo: "POST",
        cuerpo: {
          nombre: datos.nombre,
          grupo: datos.grupo,
          precioEuros: Number(datos.precioEuros || 0),
          controlarStock: datos.controlarStock,
          stockUnidades: Number(datos.stockUnidades || 0),
        },
      });
      setDatos({ nombre: "", grupo: datos.grupo, precioEuros: "", controlarStock: false, stockUnidades: "" });
      alCambiar();
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : t("ba.c.fallo"));
    } finally {
      setEnviando(false);
    }
  }

  async function alternar(p: ProductoBarra) {
    await api(`/barra/productos/${p.id}`, { metodo: "PATCH", cuerpo: { activo: !p.activo } });
    alCambiar();
  }

  return (
    <Modal
      titulo={t("ba.carta")}
      alCerrar={alCerrar}
      acciones={
        <button className="boton secundario" onClick={alCerrar}>
          {t("g.cerrar")}
        </button>
      }
    >
      <Aviso tipo="error">{error}</Aviso>

      <div className="rejilla dos">
        <Campo etiqueta={t("ba.c.nombre")}>
          <input
            value={datos.nombre}
            onChange={(e) => setDatos((d) => ({ ...d, nombre: e.target.value }))}
            placeholder={t("ba.c.nombreEjemplo")}
          />
        </Campo>
        <Campo etiqueta={t("ba.c.grupo")} ayuda={t("ba.c.grupoAyuda")}>
          <input value={datos.grupo} onChange={(e) => setDatos((d) => ({ ...d, grupo: e.target.value }))} />
        </Campo>
      </div>

      <div className="rejilla dos">
        <Campo etiqueta={t("ba.c.precio")}>
          <input
            type="number"
            step="0.05"
            min="0"
            value={datos.precioEuros}
            onChange={(e) => setDatos((d) => ({ ...d, precioEuros: e.target.value }))}
          />
        </Campo>
        <Campo etiqueta={t("ba.c.existencias")} ayuda={t("ba.c.existenciasAyuda")}>
          <input
            type="number"
            min="0"
            value={datos.stockUnidades}
            disabled={!datos.controlarStock}
            onChange={(e) => setDatos((d) => ({ ...d, stockUnidades: e.target.value }))}
          />
        </Campo>
      </div>

      <label className="fila" style={{ gap: 8, marginBottom: 12 }}>
        <input
          type="checkbox"
          checked={datos.controlarStock}
          onChange={(e) => setDatos((d) => ({ ...d, controlarStock: e.target.checked }))}
        />
        <span>{t("ba.c.llevarCuenta")}</span>
      </label>
      <p className="suave pequeno" style={{ marginTop: 0 }}>
        {t("ba.c.llevarCuentaNota")}
      </p>

      <button className="boton" disabled={enviando || datos.nombre.trim().length === 0} onClick={() => void crear()}>
        {enviando ? t("g.guardando") : t("ba.c.anadir")}
      </button>

      {productos.length > 0 && (
        <div className="tabla-envoltorio" style={{ marginTop: 16 }}>
          <table className="tabla">
            <thead>
              <tr>
                <th>{t("ba.c.producto")}</th>
                <th className="num">{t("ba.c.precioColumna")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {productos.map((p) => (
                <tr key={p.id} style={p.activo ? undefined : { opacity: 0.5 }}>
                  <td>
                    {p.nombre}
                    <div className="suave pequeno">
                      {p.grupo}
                      {p.controlarStock ? ` · ${t("ba.c.quedan", { n: p.stockUnidades })}` : ""}
                    </div>
                  </td>
                  <td className="num tabular">{creditos(p.precioCentimos)}</td>
                  <td>
                    <button className="boton fantasma" onClick={() => void alternar(p)}>
                      {p.activo ? t("ba.c.quitar") : t("ba.c.reponer")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
